import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import type { SingleValue } from "react-select";

import { Meta } from "@/@types/paginated";
import { Input } from "@/components/ui/input";
import { useGetStoreOperatorsQuery } from "@/services/store-access-query";
import { getSelectClasses } from "@/utils/filter-styles";
import { SelectInput } from "components/Select";

import { useStoreAccessFilterActions } from "../../filter/hooks/actions";
import { useStoreAccessFilterState } from "../../filter/hooks/filterState";
import { extractErrorMessage, isStoreConnectionError } from "../../hooks/utils";
import { DataTable } from "../shared/data-table";

import {
  OPERATOR_CLASSIFICATION_OPTIONS,
  OperatorClassificationOption,
  SITUATION_OPTIONS,
  SituationOption,
} from "./types";
import { useOperatorColumns } from "./use-operator-columns";

export default function OperatorsTab() {
  const { storeCode, operators } = useStoreAccessFilterState();
  const { updateTabFilter } = useStoreAccessFilterActions();

  const columns = useOperatorColumns({ storeCode: storeCode ?? 0 });

  const {
    data: accessData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetStoreOperatorsQuery(
    {
      storeCode: storeCode!,
      search: operators.search || undefined,
      code: operators.code || undefined,
      classification: operators.classification || undefined,
      cpfFilter: operators.cpfFilter,
      blocked: operators.blocked ?? undefined,
      situation: operators.situation || undefined,
      sortBy: operators.sortBy,
      sortOrder: operators.sortOrder,
      page: operators.page,
      limit: operators.perPage,
    },
    { skip: !storeCode },
  );

  const { data = [], meta = {} } = accessData ?? {};

  const connectionError =
    isError && isStoreConnectionError(error)
      ? extractErrorMessage(error) ||
        `Loja ${storeCode} não está acessível no momento.`
      : undefined;

  // Debounce de busca por código. Recriado se `storeCode` mudar (após reset
  // de filtros na troca de loja, qualquer digitação pendente fica inválida).
  const debouncedUpdateCode = useMemo(
    () =>
      debounce((value: string) => {
        updateTabFilter({ tab: "operators", key: "code", value });
        updateTabFilter({ tab: "operators", key: "page", value: 1 });
      }, 500),
    // updateTabFilter vem do hook — dispatch é estável, evitamos recriar o debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeCode],
  );
  useEffect(() => () => debouncedUpdateCode.cancel(), [debouncedUpdateCode]);

  const selectedClassification = useMemo(
    () =>
      OPERATOR_CLASSIFICATION_OPTIONS.find(
        opt => opt.value === operators.classification,
      ) || null,
    [operators.classification],
  );

  const handleClassificationChange = (
    option: SingleValue<OperatorClassificationOption>,
  ) => {
    updateTabFilter({
      tab: "operators",
      key: "classification",
      value: option?.value ?? null,
    });
    updateTabFilter({ tab: "operators", key: "page", value: 1 });
  };

  const selectedSituation = useMemo(
    () =>
      SITUATION_OPTIONS.find(opt => opt.value === operators.situation) || null,
    [operators.situation],
  );

  const handleSituationChange = (option: SingleValue<SituationOption>) => {
    updateTabFilter({
      tab: "operators",
      key: "situation",
      value: option?.value ?? null,
    });
    updateTabFilter({ tab: "operators", key: "page", value: 1 });
  };

  const tabFilters = (
    <>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Código</span>
        <Input
          key={storeCode ?? "no-store"}
          placeholder="Ex: 012CX"
          defaultValue={operators.code}
          onChange={e => debouncedUpdateCode(e.target.value)}
          className="h-8 w-[180px]"
          aria-label="Buscar operador por código"
          data-testid="operators-tab-filters"
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Classificação</span>
        <SelectInput
          options={OPERATOR_CLASSIFICATION_OPTIONS}
          placeholder="Todas"
          value={selectedClassification}
          onChange={handleClassificationChange}
          className={`${getSelectClasses(!!operators.classification)} h-8 min-w-[180px]`}
          classNamePrefix="an-simple-select"
          isClearable
          aria-label="Filtrar operadores por classificação"
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Situação</span>
        <SelectInput
          options={SITUATION_OPTIONS as unknown as SituationOption[]}
          placeholder="Todas"
          value={selectedSituation}
          onChange={handleSituationChange}
          className={`${getSelectClasses(!!operators.situation)} h-8 min-w-[180px]`}
          classNamePrefix="an-simple-select"
          isClearable
          aria-label="Filtrar operadores por situação"
        />
      </div>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      meta={meta as Meta}
      isLoading={isLoading}
      connectionError={connectionError}
      onRetry={connectionError ? refetch : undefined}
      filterSlot={tabFilters}
    />
  );
}
